import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { UserRole } from '../../types';
import { theme } from '../../design/theme';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const isSuperAdminRoute = window.location.pathname === '/super-admin/login';
  const isEnterpriseRoute = window.location.pathname === '/login/enterprise';
  const isIndividualRoute = window.location.pathname === '/login/individual';
  const isEmployeeRoute = window.location.pathname === '/login/employee';

  const getTitle = () => {
    if (isSuperAdminRoute) return 'Admin Gateway';
    if (isEnterpriseRoute) return 'Enterprise Login';
    if (isIndividualRoute) return 'Individual Login';
    if (isEmployeeRoute) return 'Employee Portal';
    return 'Exibrain';
  };

  const getSubtitle = () => {
    if (isSuperAdminRoute) return 'Secure Infrastructure Access';
    if (isEnterpriseRoute) return 'Organization Resource Gateway';
    if (isIndividualRoute) return 'Personal Brain Access';
    if (isEmployeeRoute) return 'Corporate Workspace Access';
    return 'Enterprise Resource Gateway';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const user = await login(email, password);
      
      if (isSuperAdminRoute) {
        if (user.role === UserRole.SUPER_ADMIN) {
          navigate('/super/dashboard');
          return;
        } else {
          // If a non-super-admin tries to login through the private route, treat as error
          setError('Unauthorized access');
          return;
        }
      }

      if (user.role === UserRole.SUPER_ADMIN && !isSuperAdminRoute) {
        // Prevent Super Admin from logging in through the public route for security
        setError('Please use the secure administrator gateway.');
        return;
      }

      if (user.role === UserRole.SUPER_ADMIN) navigate('/super/dashboard');
      else if (user.role === UserRole.ENTERPRISE_ADMIN) navigate('/enterprise/dashboard');
      else if (user.role === UserRole.EMPLOYEE) navigate('/employee/dashboard');
      else navigate('/user/balance');
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.background.muted,
      padding: theme.spacing.lg
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: theme.colors.background.surface,
        padding: theme.spacing.xl,
        borderRadius: '12px',
        boxShadow: theme.shadows.lg,
        border: isSuperAdminRoute ? `2px solid ${theme.colors.brand.main}` : `1px solid ${theme.colors.neutral[200]}`
      }}>
        <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
          <h1 style={{ 
            ...theme.typography.h1, 
            fontSize: '28px', 
            color: theme.colors.brand.main,
            margin: `0 0 ${theme.spacing.xs} 0`
          }}>
            {getTitle()}
          </h1>
          <p style={{ ...theme.typography.body, color: theme.colors.neutral[500] }}>
            {getSubtitle()}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
          {error && (
            <div style={{
              padding: theme.spacing.sm,
              backgroundColor: theme.colors.critical.light,
              color: theme.colors.critical.dark,
              borderRadius: '6px',
              fontSize: '14px',
              border: `1px solid ${theme.colors.critical.main}40`
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
            <label style={{ ...theme.typography.caption, fontWeight: '600', color: theme.colors.neutral[700] }}>
              Email Address
            </label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              placeholder="name@company.com"
              style={{
                padding: theme.spacing.md,
                borderRadius: '6px',
                border: `1px solid ${theme.colors.neutral[300]}`,
                fontSize: '15px',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = theme.colors.brand.main}
              onBlur={(e) => e.target.style.borderColor = theme.colors.neutral[300]}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ ...theme.typography.caption, fontWeight: '600', color: theme.colors.neutral[700] }}>
                Password
              </label>
              <a href="#" style={{ fontSize: '12px', color: theme.colors.brand.main }}>Forgot password?</a>
            </div>
            <input 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              placeholder="••••••••"
              style={{
                padding: theme.spacing.md,
                borderRadius: '6px',
                border: `1px solid ${theme.colors.neutral[300]}`,
                fontSize: '15px',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = theme.colors.brand.main}
              onBlur={(e) => e.target.style.borderColor = theme.colors.neutral[300]}
            />
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting}
            style={{
              marginTop: theme.spacing.sm,
              padding: theme.spacing.md,
              backgroundColor: theme.colors.brand.main,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '15px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.7 : 1,
              transition: 'background-color 0.2s',
              boxShadow: '0 4px 6px -1px rgba(14, 165, 233, 0.2)'
            }}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div style={{ marginTop: theme.spacing.md, fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center' }}>
          {!isSuperAdminRoute && (
            <p style={{ margin: '4px 0' }}>
              Don't have an account? <Link to="/signup" style={{ color: theme.colors.brand.main, fontWeight: '600' }}>Sign Up</Link>
            </p>
          )}
          {(isEnterpriseRoute || isIndividualRoute) && (
            <p style={{ margin: '8px 0' }}>
              <Link to="/login" style={{ color: theme.colors.neutral[500], textDecoration: 'none' }}>← Back to selection</Link>
            </p>
          )}
        </div>

        <div style={{ 
          marginTop: theme.spacing.xl, 
          textAlign: 'center', 
          borderTop: `1px solid ${theme.colors.neutral[200]}`,
          paddingTop: theme.spacing.lg
        }}>
          <p style={{ ...theme.typography.caption, color: theme.colors.neutral[500] }}>
            {isSuperAdminRoute ? 'Administrative Protocol Active' : 'Secure Login Protocol'}
          </p>
        </div>
      </div>
    </div>
  );
};
